'use client';
import { useRef, useState } from 'react';
import { ThumbsDown, ThumbsUp, type LucideIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Tooltip, TooltipAnchor, TooltipContent } from '../../components/ui/shadcn/tooltip';
import { useTooltipAnchor } from '../../components/ui/shadcn/useTooltipAnchor';
import { useToast } from '../../components/ui/Toast';
import { interpolate, useTranslation } from '../../lib/i18n';
import { cn } from '../../lib/utils';
import { REACTION_EMOJI } from '../../lib/reactions';
import type { BrainMessage, Profile, ReactionEmoji } from '../../lib/types';

/** One reaction kind on a message, as the message view folds it. */
export type MessageReaction = NonNullable<BrainMessage['reactions']>[number];

/** The signed-in account, as it appears among a message's reactors. */
export type ReactionViewer = Pick<Profile, 'id' | 'name'>;

/** What a transcript needs to let the viewer react: who is reacting, who owns the conversation (the writer
 *  of every user message that names no author), and the call that stores one change. One stable object per
 *  conversation, so memoized messages keep their identity. */
export interface ReactionControl {
  viewer: ReactionViewer;
  ownerId: number;
  toggle: (messageId: string, emoji: ReactionEmoji, add: boolean) => Promise<unknown>;
}

/** Nobody reacts to their own message (the daemon refuses it with `own_message`): a user message is the
 *  author's, a deleted account's when that account is gone, or the owner's when it names none. Assistant
 *  messages are never a person's own. */
export function writtenByViewer(message: { role: 'you' | 'elowen'; author?: { id: number }; deletedAuthorId?: number }, control: ReactionControl): boolean {
  return message.role === 'you' && (message.author?.id ?? message.deletedAuthorId ?? control.ownerId) === control.viewer.id;
}

type Wanted = ReadonlyMap<ReactionEmoji, { mine: boolean; attempt: number }>;
const NOTHING_WANTED: Wanted = new Map();

/** The viewer's reaction changes drawn before the server confirms them. Each wanted state is laid over
 *  the reactions the message currently carries; it stays until a new reactions value agrees with it (the
 *  live event folded the change in) and is dropped, with a toast, when the request fails. Holding it past
 *  the response avoids a flicker back when the response outruns the live event. */
export function useMessageReactions(messageId: string, reactions: readonly MessageReaction[], control: ReactionControl) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const attempts = useRef(0);
  const [wanted, setWanted] = useState<{ base: readonly MessageReaction[]; want: Wanted }>({ base: reactions, want: NOTHING_WANTED });
  // A new reactions value settles every wanted state it already shows (adjusting state while rendering, so
  // the agreed overlay never paints over a later server change).
  let want = wanted.want;
  if (wanted.base !== reactions) {
    want = new Map([...want].filter(([emoji, wish]) => (reactions.find((r) => r.emoji === emoji)?.mine ?? false) !== wish.mine));
    setWanted({ base: reactions, want });
  }

  const shown = want.size ? overlay(reactions, want, control.viewer) : reactions;
  const toggle = (emoji: ReactionEmoji) => {
    const add = !(shown.find((r) => r.emoji === emoji)?.mine ?? false);
    const attempt = ++attempts.current;
    setWanted((cur) => ({ ...cur, want: new Map(cur.want).set(emoji, { mine: add, attempt }) }));
    control.toggle(messageId, emoji, add).catch(() => {
      setWanted((cur) => {
        if (cur.want.get(emoji)?.attempt !== attempt) return cur;
        const next = new Map(cur.want);
        next.delete(emoji);
        return { ...cur, want: next };
      });
      toast(t.chat.reactionFailed, 'error');
    });
  };
  return { reactions: shown, toggle };
}

function overlay(reactions: readonly MessageReaction[], want: Wanted, viewer: ReactionViewer): MessageReaction[] {
  const out: MessageReaction[] = [];
  for (const reaction of reactions) {
    const wish = want.get(reaction.emoji);
    if (!wish || wish.mine === reaction.mine) out.push(reaction);
    else if (wish.mine) out.push({ ...reaction, count: reaction.count + 1, mine: true, reactors: [...reaction.reactors, viewer] });
    else if (reaction.count > 1) out.push({ ...reaction, count: reaction.count - 1, mine: false, reactors: reaction.reactors.filter((r) => r.id !== viewer.id) });
  }
  for (const [emoji, wish] of want) {
    if (wish.mine && !reactions.some((r) => r.emoji === emoji)) out.push({ emoji, count: 1, mine: true, reactors: [viewer] });
  }
  return out;
}

const ICON: Record<ReactionEmoji, LucideIcon> = { '👍': ThumbsUp, '👎': ThumbsDown };

/** The one reaction row of a message: a borderless ghost icon per kind, its count beside it once someone
 *  reacted, pressed (filled, in the primary colour) when the viewer is among the reactors, and the
 *  reactors' names in its tooltip. It sits in the message's own flow, so revealing it moves nothing.
 *
 *  With `onToggle` both kinds are offered. A message nobody reacted to marks its row `data-quiet`; the
 *  stylesheet (`chat.css`) alone owns the reveal: on a wide hovering pointer a quiet row shows while the
 *  message is hovered or holds focus, and everywhere else (touch, narrow windows) the row is always drawn.
 *  Without `onToggle` only the kinds someone used are drawn, read-only, and a message nobody reacted to draws
 *  nothing. The message that contains the row carries `group/reactable`; `className` places the row in it. */
export function MessageReactions({ reactions, onToggle, className }: {
  reactions: readonly MessageReaction[];
  onToggle?: (emoji: ReactionEmoji) => void;
  className: string;
}) {
  const { t } = useTranslation();
  // The fold drops a kind whose last reactor left, so every listed kind has a count.
  const kinds = onToggle ? REACTION_EMOJI : REACTION_EMOJI.filter((emoji) => reactions.some((reaction) => reaction.emoji === emoji));
  if (!kinds.length) return null;
  return (
    <div
      role="group"
      aria-label={t.chat.react}
      data-testid="chat-reactions"
      data-quiet={reactions.length === 0 ? '' : undefined}
      className={cn('chat-reactions flex items-center gap-0.5', className)}
    >
      {kinds.map((emoji) => (
        <ReactionButton key={emoji} emoji={emoji} reaction={reactions.find((reaction) => reaction.emoji === emoji)} onToggle={onToggle} />
      ))}
    </div>
  );
}

function ReactionButton({ emoji, reaction, onToggle }: { emoji: ReactionEmoji; reaction?: MessageReaction; onToggle?: (emoji: ReactionEmoji) => void }) {
  const { t } = useTranslation();
  const anchor = useTooltipAnchor();
  const Icon = ICON[emoji];
  const label = emoji === '👍' ? t.chat.reactionUp : t.chat.reactionDown;
  const count = reaction?.count;
  const mine = reaction?.mine ?? false;
  const names = reaction?.reactors.map((r) => (r.id === 'agent' ? t.chat.roleElowen : r.name)).join(', ') ?? '';
  return (
    <Tooltip open={anchor.open} onOpenChange={anchor.onOpenChange}>
      <TooltipAnchor asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // The visible count belongs to the name, or a screen reader hears the kind without how many chose it.
          aria-label={count ? `${label} ${count}` : label}
          aria-pressed={mine}
          aria-disabled={onToggle ? undefined : true}
          aria-describedby={anchor.open ? anchor.tooltipId : undefined}
          data-reaction={emoji}
          onClick={onToggle ? () => onToggle(emoji) : undefined}
          className={cn('gap-1 px-2 tabular-nums aria-pressed:text-primary', !onToggle && 'cursor-default hover:bg-transparent hover:text-muted-foreground aria-pressed:hover:text-primary')}
          {...anchor.hover}
          {...anchor.focus}
        >
          <Icon size={16} aria-hidden fill={mine ? 'currentColor' : 'none'} />
          {count ? <span className="text-small">{count}</span> : null}
        </Button>
      </TooltipAnchor>
      <TooltipContent id={anchor.tooltipId} side="top" className="w-auto max-w-[min(--spacing(80),calc(100vw_-_--spacing(6)))] px-2 py-1">
        {names ? interpolate(t.chat.reactedBy, { names }) : label}
      </TooltipContent>
    </Tooltip>
  );
}
